import { FormInputType, SavedFormQuestion } from "@markit/common.types";
import { Icon } from "@iconify/react";
import {
  formQuestionDisplayName,
  handlePluralString,
  truncateString,
} from "@markit/common.utils";
import { DarkTheme, ThemeStyle } from "../../../hooks/useTheme";
import { Colors } from "../../../utils/colors";
import { useMemo } from "react";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";

type SavedQuestionItemProps = {
  item: SavedFormQuestion;
  onItemPress: () => void;
  rightIcon?: JSX.Element;
  disabled?: boolean;
  theme?: ThemeStyle;
  editOnPress?: () => void;
};

// To display the SavedQuestion details and providing the rightIcon for flexibility
const SavedQuestionItem = ({
  item,
  onItemPress,
  rightIcon,
  disabled,
  theme,
  editOnPress,
}: SavedQuestionItemProps) => {
  const numTimesUsed = useMemo(() => {
    return item.onProfileFollow
      ? item.eventIds.length + 1
      : item.eventIds.length;
  }, [item.eventIds, item.onProfileFollow]);

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () =>
      editOnPress
        ? [
            {
              title: "Edit Question",
              key: "edit_question",
              onPress: editOnPress,
            },
          ]
        : [],
    [editOnPress]
  );

  return (
    <div className="AlignedRowSpaced" style={{ flexGrow: 1 }}>
      <div
        onClick={onItemPress}
        className="AlignedRowSelect"
        style={{
          gap: 10,
          pointerEvents: disabled ? "none" : "all",
          opacity: disabled ? 0.4 : 1,
          flexGrow: 1,
        }}
      >
        <div className="ColumnNormalSelect" style={{ gap: 7 }}>
          <div className="AlignedRow" style={{ gap: 7, cursor: "pointer" }}>
            {item.isEssential ? (
              <div>
                <Icon
                  icon="fa:map-pin"
                  height={14}
                  style={{ color: theme?.PrimaryText.color }}
                />
              </div>
            ) : item.formInput.inputType === FormInputType.EMAIL ? (
              <Icon
                icon={"ion:mail-open-sharp"}
                height={14}
                style={{ color: theme?.PrimaryText.color }}
              />
            ) : null}
            <span
              className="bodyMedium"
              style={{ ...theme?.PrimaryText, marginRight: 20 }}
            >
              {truncateString(item.formInput.label ?? "", 180, "...")}
            </span>
          </div>
          <div className="AlignedRow" style={{ gap: 7 }}>
            <span
              style={{
                color: theme ? theme.SubText.color : Colors.GRAY1,
                fontSize: 12,
              }}
            >
              {formQuestionDisplayName(item.formInput)}
            </span>
            <Icon
              icon={
                item.formInput.inputType === FormInputType.RADIO
                  ? "mdi:circle-multiple"
                  : item.formInput.inputType === FormInputType.CHECK_BOX
                    ? "mdi:checkbox-multiple-marked"
                    : item.formInput.inputType === FormInputType.TEXT_INPUT
                      ? "mdi:text-box"
                      : item.formInput.inputType === FormInputType.FILE_UPLOAD
                        ? "ion:document"
                        : "ion:mail"
              }
              height={14}
              style={{ color: Colors.GRAY1 }}
            />
          </div>
          <span
            style={{
              color: theme ? theme.SubText.color : Colors.GRAY1,
              fontSize: 12,
            }}
          >
            Used {numTimesUsed} {handlePluralString("time", numTimesUsed)}
          </span>
        </div>
      </div>
      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <div style={{ height: "100%", justifyContent: "center" }}>
          {rightIcon}
        </div>
        {editOnPress ? (
          <DropdownMenu
            dropdownItems={dropdownItems}
            trigger={["click"]}
            placement="bottomLeft"
            dark={theme === DarkTheme}
          >
            <Icon
              icon={"mdi:dots-horizontal"}
              height={24}
              style={{ color: Colors.GRAY3, marginTop: 2 }}
            />
          </DropdownMenu>
        ) : null}
      </span>
    </div>
  );
};

export default SavedQuestionItem;
